<template>
  <div class="phone-step form-screen">
    <div class="form-screen__body">
      <BaseFigureForIcon
        size="medium"
        color="grey-secondary"
        icon="arrow"
        position="left"
        is-button
        @click="$emit('back')"
      />

      <h2 class="h2 color-text-primary phone-step__title">
        {{ t("auth.phone.title") }}
      </h2>
      <BaseInput
        v-model="phone"
        type="phone"
        class="caption-medium color-text-primary phone-step__input"
        :max-length="12"
        :class="{ error: errorPhone && !isLoadingBtn }"
        @validation="handleValidation"
        @change="handleChange"
      />
      <p v-if="showError && !isLoadingBtn" class="color-error hint">
        {{ t("auth.phone.notFound") }}
      </p>
      <p v-else class="color-grey-blue caption-medium hint">
        {{ t("auth.phone.hint") }}
      </p>
    </div>
    <div class="form-screen__footer">
      <BaseButton
        :title="t('auth.phone.continue')"
        size="large"
        :disabled="!isValid || isLoadingBtn"
        color="primary"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineEmits, defineProps, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import BaseInput from "@/components/base/BaseInput.vue";
import { getPhoneNumber } from "@/utils/common";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";

const emit = defineEmits(["submit", "back"]);
const { t } = useI18n();

const props = defineProps({
  isWrongPhone: Boolean,
  isLoadingBtn: Boolean,
});

const phone = ref("");
const isValid = ref(false);
const showError = ref(false);
const errorPhone = ref(false);

const handleValidation = (validationResult: { isValidPhone: boolean }) => {
  isValid.value = validationResult.isValidPhone;
};

const handleSubmit = () => {
  if (!isValid.value) return;
  emit("submit", getPhoneNumber(phone.value));
  errorPhone.value = props.isWrongPhone;
};

const handleChange = () => {
  if (phone.value.length < 18) {
    errorPhone.value = false;
  }
};

watch(
  () => props.isWrongPhone,
  (val) => {
    if (phone.value.length === 18 && props.isWrongPhone) {
      errorPhone.value = true;
    }

    showError.value = val;

    if (showError.value) {
      isValid.value = false;
    }
  }
);
</script>

<style scoped lang="scss">
@import "@/assets/style/main";

.phone-step {
  background: var(--color-white);
  padding: 25px 24px 28px 24px;
  border-radius: 32px;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: 392px;
  min-height: 537px;

  @media (max-width: 768px) {
    // Нижний отступ карточки из макета (28px) переезжает в футер:
    // сам блок прижат к нижней кромке, а воздух под кнопкой даёт футер.
    --form-footer-gap: 28px;

    width: 100%;
    min-height: 0;
    border-radius: 0;
    // Отступы уезжают внутрь .form-screen__body, чтобы футер прилипал к самому низу.
    padding: 25px 0 0 0;

    .form-screen__body {
      padding: 0 24px;
    }

    .form-screen__footer {
      padding-left: 24px;
      padding-right: 24px;
      background: var(--color-white);
    }
  }

  &__title {
    margin: 24px 0 32px 0;
  }

  &__input {
    background: var(--color-bg-primary);
    border-radius: 12px;
    border: none;
    height: 64px;
    width: 100%;
    font-family: inherit;
    cursor: pointer;

    &:focus {
      outline: none;
    }

    &::placeholder {
      color: inherit;
      opacity: 1;
    }
  }

  &__submit-button {
    bottom: 28px;
    width: 100%;
    padding: 20px 0 18px 0;
    border-radius: 30px;
    border: none;
    background: var(--color-secondary);
    text-transform: uppercase;

    &:disabled {
      background: var(--color-grey-blue);
      cursor: not-allowed;
    }
  }
}

.hint {
  text-align: left;
  margin: 24px 0 0 0;
}

.error :deep(.phone-input) {
  border: 1px solid var(--color-danger-primary);
  color: var(--color-danger-primary);
}
</style>
